import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import classNames from 'classnames'
import { setCookie } from 'cookies-next'
import { Case, Else, If, Switch, Then, When } from 'react-if'
import { GuildScore } from '@/atoms/GuildScore'
import { LoadingSpinnerIcon } from '@/atoms/Icons/LoadingSpinnerIcon'
import { ClosedCaptionsBadge, ContentRatingBadge, HighDefinitionBadge } from '@/atoms/MetadataBadge'
import { PaddedContainer } from '@/atoms/PaddedContainer'
import { CaptionMD, CaptionSM, TitleXS } from '@/atoms/Text'
import { CONTENT_SHARED_ID } from '@/constants/cookies'
import { Durations } from '@/constants/durations'
import { paths } from '@/constants/paths'
import { projectTypes } from '@/constants/projectTypes'
import { useProjectMeta } from '@/contexts/ProjectMetaContext'
import { BoundedImage } from '@/molecules/BoundedImage'
import { LoginEmailInput } from '@/organisms/LoginEmailInput'
import { ResponsiveBackgroundContainer } from '@/organisms/WatchProjectHero/ResponsiveBackgroundContainer'
import {
  CatalogSeriesRating,
  CatalogTitle,
  findHydraExternalId,
  findTitleProjectSlug,
  formatNonTitleLikeLandscapeImagePath,
  formatNonTitleLikeVerticalImagePath,
  formatTitleLogo,
  formatTitleReleaseYear,
} from '@/services/ContentCatalog'
import { claimOneToManyShareLink, ShareLink } from '@/services/ContentShareService'
import { useUser } from '@/services/UserService'
import { useSafeTrack } from '@/utils/analytics'
import { buildLoginUrl, getAccessTokenFromClient } from '@/utils/auth-utils'
import { logger } from '@/utils/logging'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { Translate, useTranslate } from '@/utils/translate/translate-client'

export interface ContentSharedViewDisplayFields {
  title: string
  subTitle?: string
}

interface ContentSharedViewProps {
  shareLink: ShareLink
  watchableTitle: CatalogTitle
  displayFields: ContentSharedViewDisplayFields
}

export const OneToManyContentSharedView: React.FC<ContentSharedViewProps> = ({
  shareLink,
  watchableTitle,
  displayFields,
}) => {
  const track = useSafeTrack()
  const contentRef = useRef<HTMLDivElement>(null)
  const isClaimingRef = useRef<boolean>(false)
  const { t } = useTranslate('watch')
  const { isLoggedIn, userId } = useUser()
  const router = useSafeRouter()
  const [expanded, setExpanded] = useState(false)
  const isTruncated = useTruncated()
  const {
    metadata,
    projectType: projectMetaType,
    theaterDescription,
    highestScore,
    logoCloudinaryPath,
    title,
  } = useProjectMeta()
  const contentRating =
    'ratings' in watchableTitle && Array.isArray(watchableTitle.ratings)
      ? watchableTitle.ratings.find((rating: CatalogSeriesRating) => !!rating)
      : undefined

  const slug = useMemo(() => findTitleProjectSlug(watchableTitle), [watchableTitle])
  const hydraId = useMemo(() => findHydraExternalId(watchableTitle), [watchableTitle])

  const loginUrl = buildLoginUrl(router.asPath)

  const handleReadMoreOrLess = () => {
    setExpanded(!expanded)
  }

  const redirect = useCallback(() => {
    if (slug && hydraId) {
      router.replace(`${paths.watch.index}/${slug}/episode/${hydraId}`)
    } else {
      logger().error(`Cannot redirect to the shared watchable directly because slug and or id are missing!`, {
        watchableTitle,
        slug,
        hydraId,
      })
      router.replace(paths.watch.index)
    }

    const html = window.document.getElementsByTagName('html')[0]
    if (!html) return

    setTimeout(() => {
      html.style.height = 'initial'
      html.style.overflow = 'initial'
    }, 100)
  }, [router, slug, hydraId, watchableTitle])

  useEffect(() => {
    if (!slug || !hydraId) {
      logger().error(`Cannot claim a share link because the content catalog title is missing a slug or id!`, {
        slug,
        hydraId,
        watchableTitle,
        shareLink,
      })
    }
  }, [slug, hydraId, watchableTitle, shareLink, t])

  useEffect(() => {
    if (isClaimingRef.current) return

    if (!isLoggedIn || !userId) {
      setCookie(CONTENT_SHARED_ID, shareLink.id, { maxAge: Durations.ONE_WEEK_IN_SECONDS })
      return
    }

    // Check if this is the same user that created the share link
    if (userId === shareLink.user_id) return redirect()

    const accessToken = getAccessTokenFromClient()

    isClaimingRef.current = true

    claimOneToManyShareLink({ accessToken, shareLinkId: shareLink.id })
      .then((claim) => {
        track('Guild Content Share Link Claimed', {
          shareLink,
          claim,
          sharerUserId: shareLink.user_id,
          claimerUserId: userId,
          titleId: shareLink.content_id,
          type: 'one-to-many',
        })

        redirect()
      })
      .catch((err) => {
        isClaimingRef.current = false
        logger().error('An error occurred when trying to claim a share link!', { shareLink }, err)
      })
  }, [isLoggedIn, userId, redirect, shareLink, track])

  const fallbackImage = shareLink.metadata.opengraph_image
  const isSeriesContent = projectMetaType === projectTypes.series
  const logoImageUrl = formatTitleLogo(watchableTitle)
  const releaseYear = formatTitleReleaseYear(watchableTitle)

  const backgroundImageUrl =
    (isSeriesContent && getSeriesLandscapeBackdrop(title)) ||
    formatNonTitleLikeLandscapeImagePath(watchableTitle) ||
    fallbackImage

  const backgroundTabletImageUrl =
    (isSeriesContent && getSeriesLandscapeBackdrop(title)) ||
    formatNonTitleLikeLandscapeImagePath(watchableTitle) ||
    fallbackImage

  const backgroundMobileImageUrl =
    (isSeriesContent && getSeriesSquarePortraitNonTitleArt(title)) ||
    formatNonTitleLikeVerticalImagePath(watchableTitle, ['1:1', '4:3']) ||
    fallbackImage

  return (
    <main>
      <div
        className={classNames([
          'flex flex-col gap-4 relative w-full lg:flex-row lg:flex-wrap md:min-h-[50vh] lg:min-h-[70vh] xl:min-h-[75vh] 2xl:min-h-[630px]',
        ])}
        ref={contentRef}
      >
        <ResponsiveBackgroundContainer
          cloudinaryImgPath={backgroundImageUrl}
          cloudinaryTabletImgPath={backgroundTabletImageUrl}
          cloudinaryMobileImgPath={backgroundMobileImageUrl}
          theaterBackgroundPosition={'bg-top'}
          contentRef={contentRef}
        />

        <PaddedContainer fluid className="flex items-center justify-center">
          <div className="relative mt-[23vh] w-full py-24 md:mt-10 md:max-w-[600px] md:pb-0 xl:max-w-[750px] se:mt-[20vh]">
            {(() => {
              const logoPath = logoImageUrl?.cloudinaryPath || logoCloudinaryPath

              if (!logoPath) {
                return null
              }

              return (
                <BoundedImage
                  className="mb-2 flex items-center justify-center md:justify-start"
                  alt={watchableTitle.title}
                  path={logoPath}
                  width={600}
                  height={104}
                />
              )
            })()}
            <div className="flex flex-col flex-wrap items-center justify-start gap-2 !text-core-gray-400 md:mb-6 md:items-start">
              <div className="flex flex-row flex-wrap items-center justify-center gap-1 md:justify-start lg:gap-2">
                {releaseYear && (
                  <>
                    <CaptionSM className="sm:photon-caption-md">{releaseYear}</CaptionSM>
                    <CaptionSM className="sm:photon-caption-md">•</CaptionSM>
                  </>
                )}
                <CaptionSM className="sm:photon-caption-md whitespace-nowrap">
                  <Switch>
                    <Case condition={projectMetaType === projectTypes.series}>
                      <Translate i18nKey="tvSeries" t={t}>
                        TV Series
                      </Translate>
                    </Case>
                    <Case condition={projectMetaType === projectTypes.movie}>
                      <Translate i18nKey="movie" t={t}>
                        Movie
                      </Translate>
                    </Case>
                    <Case condition={projectMetaType === projectTypes.podcast}>
                      <Translate i18nKey="podcast" t={t}>
                        Podcast
                      </Translate>
                    </Case>
                    <Case condition={projectMetaType === projectTypes.special}>
                      <Translate i18nKey="special" t={t}>
                        Special
                      </Translate>
                    </Case>
                  </Switch>
                </CaptionSM>
                {metadata?.genres?.length && metadata.genres.length > 0 && (
                  <CaptionSM className="sm:photon-caption-md">•</CaptionSM>
                )}
                {metadata?.genres?.map((genre: string, index) => {
                  return (
                    <div className="flex gap-1 lg:gap-2" key={genre}>
                      <CaptionSM className="sm:photon-caption-md">{genre}</CaptionSM>
                      <When condition={index < metadata.genres.length - 1}>
                        <CaptionSM className="sm:photon-caption-md">•</CaptionSM>
                      </When>
                    </div>
                  )
                })}
              </div>
              <div className="flex flex-row flex-wrap items-center justify-start gap-2">
                <ContentRatingBadge
                  contentRating={contentRating?.rating}
                  className="rounded-xl border-0 bg-[#0000004d] px-3 py-1 text-white backdrop-blur-xl"
                />
                <HighDefinitionBadge
                  slug={slug ?? ''}
                  className="rounded-xl border-0 bg-[#0000004d] px-3 py-1 text-white backdrop-blur-xl"
                />
                <ClosedCaptionsBadge className="rounded-xl border-0 bg-[#0000004d] px-3 py-1 text-white backdrop-blur-xl" />
                <When condition={highestScore}>
                  <GuildScore guildScore={highestScore} />
                </When>
              </div>
              <div className="mb-2 flex max-w-[500px] flex-col items-center gap-2 md:items-start">
                <CaptionMD
                  id="show-description"
                  className={classNames('text-center md:text-left transition-all duration-600 ease-in-out', {
                    'max-h-[60px] line-clamp-2': !expanded,
                    'overflow-hidden max-h-96': expanded,
                  })}
                  weight="medium"
                  color="core-gray-400"
                >
                  {theaterDescription}
                </CaptionMD>
                <When condition={isTruncated}>
                  <button onClick={handleReadMoreOrLess} className="inline-block">
                    <CaptionMD
                      className="cursor-pointer transition-all duration-300 ease-in-out hover:brightness-110"
                      color="oxide-bright"
                      weight="medium"
                    >
                      <If condition={expanded}>
                        <Then>
                          <Translate i18nKey="readLessv3" t={t}>
                            Read less
                          </Translate>
                        </Then>
                        <Else>
                          <Translate i18nKey="readMorev3" t={t}>
                            Read more
                          </Translate>
                        </Else>
                      </If>
                    </CaptionMD>
                  </button>
                </When>
              </div>
            </div>
            <TitleXS
              weight="bold"
              color="white"
              className="md:photon-title-sm se:photon-title-xxs mx-auto mb-2 text-balance text-center md:mx-0 md:mb-0 md:text-left"
            >
              {t('youHaveBeenInvitedToWatchThanksToGuildMemberFree', `You've been invited to watch {{ name }} FREE `, {
                name: displayFields.title,
              })}
            </TitleXS>
            <div className="flex flex-col items-center md:items-start lg:pt-4">
              <When condition={!isLoggedIn}>
                <div className="mt-6 flex w-full max-w-[600px] flex-col items-center gap-2 md:mt-0 md:items-start">
                  <LoginEmailInput
                    loginOrigin="shared"
                    variant="centered-white"
                    contentShareId={shareLink.id}
                    className="md:w-full md:flex-row"
                  />
                  <CaptionMD className="my-2" weight="semibold" color="core-gray-300">
                    <Translate i18nKey="alreadyAngelMemberLoginSpace" t={t}>
                      Already an Angel Member?{'   '}
                      <a href={loginUrl} className="ml-1 text-oxide-primary">
                        Login
                      </a>
                    </Translate>
                  </CaptionMD>
                </div>
              </When>
              <When condition={isLoggedIn}>
                <div className="flex min-h-[136px] w-full max-w-[600px] flex-col items-center gap-4 md:min-h-[120px] md:gap-2 lg:mt-8 lg:min-h-[98px]">
                  <TitleXS
                    weight="bold"
                    color="core-gray-100"
                    className="md:photon-title-sm lg:photon-title-md xl:photon-title-lg"
                  >
                    {t('claimLinkLoading', 'Preparing to watch...')}
                  </TitleXS>
                  <LoadingSpinnerIcon size={48} color="white" />
                </div>
              </When>
            </div>
            {/* Commenting this out to simply test old vs new view and then we can re-test this addition */}
            {/* {!isDismissed && (
                <div className="fixed inset-x-0 bottom-0 z-50 flex w-full flex-col justify-between gap-2 border-t border-solid border-core-gray-700 bg-core-gray-950 p-4 sm:px-8 md:flex-row md:px-12 md:py-8 xl:px-16">
                  <div className="flex flex-col items-center md:items-start lg:justify-center">
                    <TitleXS
                      weight="bold"
                      color="white"
                      className="md:photon-title-sm se:photon-title-xxs mx-auto mb-2 text-balance text-center md:mx-0 md:text-left"
                    >
                      {t(
                        'youHaveBeenInvitedToWatchThanksToGuildMember',
                        `You've been invited to watch {{ name }} thanks to a Guild member. Enjoy the show! `,
                        {
                          name: displayFields.title,
                        },
                      )}
                    </TitleXS>
                    <CaptionSM
                      className="md:photon-caption-md se:photon-caption-xs text-center md:text-left"
                      color="core-gray-300"
                      weight="semibold"
                    >
                      {t(
                        'joinGuildTodaySpecialOffer',
                        'Join the Guild today with a special offer and share it forward!',
                      )}
                    </CaptionSM>
                    <When condition={!isLoggedIn}>
                      <div className="mt-6 flex w-full max-w-[600px] flex-col items-center gap-2 md:mt-4 md:flex-row md:items-start md:gap-4 se:mt-2">
                        <LinkButton
                          href={paths.guild.offer.million}
                          onClick={handleGuildJoinButtonClick}
                          variant="white"
                          className="flex max-h-12 w-full items-center gap-[6px] rounded-xl px-6 py-[14px] md:w-fit se:max-h-10 se:px-4"
                        >
                          <GuildIconMono size={24} color="core-gray-950" />
                          <LabelMD weight="semibold" className="se:photon-label-sm mt-[2.25px]" color="core-gray-950">
                            {t('specialOffer', 'Special Offer')}
                          </LabelMD>
                        </LinkButton>
                        <Button
                          variant="white"
                          onClick={() => setIsDismissed(true)}
                          outline
                          className="flex max-h-12 w-full items-center gap-[6px] rounded-xl px-6 py-[14px] md:w-fit se:max-h-10 se:px-4"
                        >
                          <LabelMD weight="semibold" className="se:photon-label-sm mt-[2.25px]" color="white">
                            {t('notNow', 'Not Now')}
                          </LabelMD>
                        </Button>
                      </div>
                    </When>
                  </div>
                </div>
              )} */}
          </div>
        </PaddedContainer>
      </div>
    </main>
  )
}

const useTruncated = () => {
  const [isTruncated, setIsTruncated] = useState(false)

  useEffect(() => {
    const handleResize = () => {
      const element = document.getElementById('show-description')

      if (element) {
        setIsTruncated(element.offsetHeight < element.scrollHeight || element.offsetWidth < element.scrollWidth)
      }
    }

    window.addEventListener('resize', handleResize)
    handleResize()
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return isTruncated
}

const getCloudinaryPath = (backdrop: unknown): string | null => {
  if (
    backdrop &&
    typeof backdrop === 'object' &&
    'cloudinaryPath' in backdrop &&
    typeof backdrop.cloudinaryPath === 'string'
  ) {
    return backdrop.cloudinaryPath
  }
  return null
}

const getSeriesLandscapeBackdrop = (title: unknown): string | null => {
  if (title && typeof title === 'object' && 'landscapeBackdrop' in title) {
    return getCloudinaryPath(title.landscapeBackdrop)
  }
  return null
}

const getSeriesSquarePortraitNonTitleArt = (title: unknown): string | null => {
  if (title && typeof title === 'object' && 'squarePortraitNonTitleArt' in title) {
    return getCloudinaryPath(title.squarePortraitNonTitleArt)
  }
  return null
}
